import React from 'react';

import {assert} from 'Asserts';
import * as FileConstants from 'folder-items/FileConstants';
import {isAffiliatedSchemaTag, SchemaTag, UnaffiliatedSchemaTag} from 'folder-items/FileConstants';
import EntryLinkInput from 'metadata/EntryLinkInput';
import TagValueViewer from 'metadata/TagValueViewer';
import StructuredTableDatetimeInputContainer from 'notebook/structured-tables/components/StructuredTableDatetimeInputContainer';
import tableMultiKeyDownHandler from 'notebook/tables/tableMultiKeyDownHandler';
import StorableLinkInput from 'samples/StorableLinkInput';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import * as SchemaFieldUtil from 'schemas/fields/SchemaFieldUtil';
import BioentityLinkInput from 'schemas/tags/BioentityLinkInput';
import ControlledBioentityLinkInput from 'schemas/tags/ControlledBioentityLinkInput';
import DatetimeValueEditor from 'schemas/tags/DatetimeValueEditor';
import DateValueEditor from 'schemas/tags/DateValueEditor';
import SelectorInput from 'schemas/tags/SelectorInput';
import * as TagUtil from 'schemas/tags/TagUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ChipSize} from 'ui-library/chips/Chip';
import {convertUTCDatetimeToLocal} from 'util/DateUtil';
import * as logging from 'util/Logging';
import {ElementKeyboardEvent} from 'util/Types';

interface InlineCellTagValueEditorProps<T extends SchemaTag | UnaffiliatedSchemaTag> {
  onBlur?: () => void;
  onEscape: () => void;
  onSetTagValue: (tagValue: T, enterKey: boolean) => void;
  onTabKey?: (e: ElementKeyboardEvent) => void;
  onUpdateQuery: (query: string) => void;
  onUpdateTagValue: (tagValue: T) => void;
  query: string;
  tagValue: T;
  autoFocus?: boolean;
  // This will typically be rendered inside a table cell. If that's not the case, set this to true
  isOutsideTable?: boolean;
  areChipsClickable?: boolean;
}

interface State<T extends SchemaTag | UnaffiliatedSchemaTag> {
  tagValue: T;
}

/**
 * Editor for tag values in table cells. Handles tag values that are neither string-based nor edited in
 * modals nor dropdown tags.
 */
class InlineCellTagValueEditor<T extends SchemaTag | UnaffiliatedSchemaTag> extends React.Component<
  InlineCellTagValueEditorProps<T>,
  State<T>
> {
  override state: State<T> = {
    tagValue: this.props.tagValue,
  };

  // For older components: we manage the tag selection here in local state
  _handleSetTagValue = (tagValue: T): void => {
    this.setState({
      tagValue: {
        ...this.state.tagValue,
        ...tagValue,
      },
    });
  };

  // For newer components: the tag selection is managed higher in the tree (specifically,
  // in FormulaEditable, which uses SpreadsheetStore)
  _handleSetControlledTagValue = (tagValue: Partial<T>): void => {
    this.props.onUpdateTagValue({
      ...this.props.tagValue,
      ...tagValue,
    });
  };

  _handleKeyDownForTagValue(e: ElementKeyboardEvent, tagValue: T): void {
    tableMultiKeyDownHandler({
      event: e,
      value: tagValue,
      onSetValue: this.props.onSetTagValue,
      onEscape: this.props.onEscape,
      onTabKey: this.props.onTabKey,
    });
  }
  _handleKeyDown = (e: ElementKeyboardEvent): void => {
    this._handleKeyDownForTagValue(e, this.state.tagValue);
  };

  _handleControlledKeyDown = (e: ElementKeyboardEvent): void => {
    const fieldType = TagUtil.getFullFieldType(this.props.tagValue.tagSchemaField.fieldType);
    const linksKey = TagUtil.getLinksKeyFromFieldType(fieldType.type);
    if (linksKey && !TagUtil.tagHasLink(this.props.tagValue)) {
      // If the tag is empty, save the search query
      this._handleKeyDownForTagValue(e, {
        ...this.props.tagValue,
        value: this.props.query,
      });
    } else {
      this._handleKeyDownForTagValue(e, this.props.tagValue);
    }
  };

  override render(): JSX.Element | null {
    const autoFocus = this.props.autoFocus ?? true;
    const defaultValue = this.state.tagValue;

    if (
      isAffiliatedSchemaTag(defaultValue) &&
      SchemaFieldUtil.isUneditableField(defaultValue.tagSchemaField)
    ) {
      return <TagValueViewer tag={defaultValue} />;
    }

    const fieldType = TagUtil.getFullFieldType(defaultValue.tagSchemaField.fieldType);
    switch (fieldType.type) {
      case FIELD_TYPES.BIOENTITY_LINK.type:
        return (
          <div
            className="inline-cell-tag-value-editor"
            {...getTestProps({component: 'InlineCellTagValueEditor', element: 'root'})}
          >
            <ControlledBioentityLinkInput
              autoFocus={autoFocus}
              chipSize={ChipSize.TABLE_CELL}
              inputValue={this.props.query}
              placeholder="Entity Link"
              onBlur={this.props.onBlur}
              onChange={this._handleSetControlledTagValue}
              onKeyDown={this._handleControlledKeyDown}
              onUpdateInputValue={this.props.onUpdateQuery}
              isOutsideTable={this.props.isOutsideTable}
              tag={this.props.tagValue}
              areChipsClickable={this.props.areChipsClickable}
            />
          </div>
        );
      case FIELD_TYPES.PART_LINK.type:
        return (
          <div
            className="inline-cell-tag-value-editor"
            {...getTestProps({component: 'InlineCellTagValueEditor', element: 'root'})}
          >
            <BioentityLinkInput
              autoFocus={autoFocus}
              chipSize={ChipSize.TABLE_CELL}
              onBlur={this.props.onBlur}
              onChange={this._handleSetTagValue}
              onKeyDown={this._handleKeyDown}
              placeholder="Part Link"
              tag={defaultValue}
              areChipsClickable={this.props.areChipsClickable}
            />
          </div>
        );
      case FIELD_TYPES.TRANSCRIPTION_LINK.type:
        return (
          <div
            className="inline-cell-tag-value-editor"
            {...getTestProps({component: 'InlineCellTagValueEditor', element: 'root'})}
          >
            <BioentityLinkInput
              autoFocus={autoFocus}
              chipSize={ChipSize.TABLE_CELL}
              onBlur={this.props.onBlur}
              onChange={this._handleSetTagValue}
              onKeyDown={this._handleKeyDown}
              placeholder="Transcription Link"
              tag={defaultValue}
              areChipsClickable={this.props.areChipsClickable}
            />
          </div>
        );
      case FIELD_TYPES.TRANSLATION_LINK.type:
        return (
          <div
            className="inline-cell-tag-value-editor"
            {...getTestProps({component: 'InlineCellTagValueEditor', element: 'root'})}
          >
            <ControlledBioentityLinkInput
              autoFocus={autoFocus}
              chipSize={ChipSize.TABLE_CELL}
              inputValue={this.props.query}
              placeholder="Translation Link"
              onBlur={this.props.onBlur}
              onChange={this._handleSetControlledTagValue}
              onKeyDown={this._handleControlledKeyDown}
              onUpdateInputValue={this.props.onUpdateQuery}
              isOutsideTable={this.props.isOutsideTable}
              tag={this.props.tagValue}
              areChipsClickable={this.props.areChipsClickable}
            />
          </div>
        );
      case FIELD_TYPES.AA_PART_LINK.type:
        return (
          <div
            className="inline-cell-tag-value-editor"
            {...getTestProps({component: 'InlineCellTagValueEditor', element: 'root'})}
          >
            <BioentityLinkInput
              autoFocus={autoFocus}
              chipSize={ChipSize.TABLE_CELL}
              onBlur={this.props.onBlur}
              onChange={this._handleSetTagValue}
              onKeyDown={this._handleKeyDown}
              placeholder="Part Link"
              tag={defaultValue}
              areChipsClickable={this.props.areChipsClickable}
            />
          </div>
        );
      case FIELD_TYPES.BASE_MOLECULE_LINK.type:
        return (
          <div
            className="inline-cell-tag-value-editor"
            {...getTestProps({component: 'InlineCellTagValueEditor', element: 'root'})}
          >
            <BioentityLinkInput
              autoFocus={autoFocus}
              chipSize={ChipSize.TABLE_CELL}
              onBlur={this.props.onBlur}
              onChange={this._handleSetTagValue}
              onKeyDown={this._handleKeyDown}
              placeholder="Base Molecule Link"
              tag={defaultValue}
              areChipsClickable={this.props.areChipsClickable}
            />
          </div>
        );
      case FIELD_TYPES.STORABLE_LINK.type:
        assert(
          isAffiliatedSchemaTag(this.props.tagValue),
          'Unable to use Batch/Storable link input with an unaffiliated schema tag'
        );
        return (
          <div
            className="inline-cell-tag-value-editor"
            {...getTestProps({component: 'InlineCellTagValueEditor', element: 'root'})}
          >
            <StorableLinkInput
              autoFocus={autoFocus}
              chipSize={ChipSize.TABLE_CELL}
              defaultValue={this.props.tagValue}
              onBlur={this.props.onBlur}
              onChange={(tagValue: Partial<FileConstants.SchemaTag>) => {
                this.props.onUpdateQuery('');
                // Hard-cast is guarded by the assert immediately above
                this._handleSetControlledTagValue(tagValue as Partial<T>);
              }}
              onKeyDown={this._handleControlledKeyDown}
              query={this.props.query}
              onUpdateQuery={this.props.onUpdateQuery}
              areChipsClickable={this.props.areChipsClickable}
              includeArchived={true}
            />
          </div>
        );
      case FIELD_TYPES.ENTRY_LINK.type:
        assert(
          isAffiliatedSchemaTag(this.props.tagValue),
          'Unable to use EntryLinkInput with an unaffiliated schema tag'
        );
        return (
          <div
            className="inline-cell-tag-value-editor"
            {...getTestProps({component: 'InlineCellTagValueEditor', element: 'root'})}
          >
            <EntryLinkInput
              areChipsClickable={this.props.areChipsClickable}
              autoFocus={autoFocus}
              chipSize={ChipSize.TABLE_CELL}
              defaultValue={this.props.tagValue}
              onBlur={this.props.onBlur}
              onChange={(tagValue: {targetEntries: Array<FileConstants.TargetEntry>; value: string}) => {
                this.props.onUpdateQuery('');
                // Hard-cast is guarded by the assert immediately above
                this._handleSetControlledTagValue(tagValue as Partial<T>);
              }}
              onKeyDown={this._handleControlledKeyDown}
              query={this.props.query}
              onUpdateQuery={this.props.onUpdateQuery}
            />
          </div>
        );
      case FIELD_TYPES.SELECTOR.type:
        assert(
          isAffiliatedSchemaTag(this.props.tagValue),
          'Unable to use SelectorInput with an unaffiliated schema tag'
        );
        return (
          <SelectorInput
            autoFocus={autoFocus}
            inputValue={this.props.query}
            onBlur={this.props.onBlur}
            onChange={(tagValue: Partial<FileConstants.SchemaTag>) => {
              // Hard-cast is guarded by the assert immediately above
              this._handleSetControlledTagValue(tagValue as Partial<T>);
            }}
            onKeyDown={this._handleControlledKeyDown}
            onUpdateInputValue={this.props.onUpdateQuery}
            tagValue={this.props.tagValue}
            isOutsideTable={this.props.isOutsideTable}
          />
        );
      case FIELD_TYPES.DATETIME.type: {
        assert(
          isAffiliatedSchemaTag(this.props.tagValue),
          'Unable to use DatetimeValueEditor with an unaffiliated schema tag'
        );
        const datetimeValueEditorComponent = (
          <DatetimeValueEditor
            autoFocus={autoFocus}
            onChange={this._handleSetControlledTagValue}
            onKeyDown={this._handleControlledKeyDown}
            defaultValue={{
              ...this.props.tagValue,
              value: convertUTCDatetimeToLocal(this.props.tagValue.value),
            }}
          />
        );
        if (this.props.isOutsideTable) {
          return datetimeValueEditorComponent;
        }
        return (
          <StructuredTableDatetimeInputContainer>
            {datetimeValueEditorComponent}
          </StructuredTableDatetimeInputContainer>
        );
      }
      case FIELD_TYPES.DATE.type: {
        assert(
          isAffiliatedSchemaTag(this.props.tagValue),
          'Unable to use DatetimeValueEditor with an unaffiliated schema tag'
        );
        const dateValueEditorComponent = (
          <DateValueEditor
            autoFocus={autoFocus}
            onChange={(tagValue: Partial<FileConstants.SchemaTag>) => {
              // Hard-cast is guarded by the assert immediately above
              this._handleSetControlledTagValue(tagValue as Partial<T>);
            }}
            onKeyDown={this._handleControlledKeyDown}
            defaultValue={this.props.tagValue}
          />
        );
        if (this.props.isOutsideTable) {
          return dateValueEditorComponent;
        }
        return (
          <StructuredTableDatetimeInputContainer>
            {dateValueEditorComponent}
          </StructuredTableDatetimeInputContainer>
        );
      }
      default:
        logging.warn(`Unexpected fieldType: ${fieldType.type}`, {
          extra: {
            defaultValue,
          },
        });
        return null;
    }
  }
}

export default InlineCellTagValueEditor;
